import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosInstance';
import logger from '../../../../utils/logger';

const ITCapabilities = ({ agentId, showNotification }) => {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchCapabilities();
  }, [agentId]);

  const fetchCapabilities = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ai/agents/${agentId}/capabilities/it`);
      if (response.data.success) {
        setCapabilities(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching IT capabilities:', error);
      showNotification?.('Failed to load IT capabilities', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(
        `/api/ai/agents/${agentId}/capabilities/it`,
        capabilities
      );
      if (response.data.success) {
        showNotification?.('IT capabilities saved successfully', 'success');
        setHasChanges(false);
      }
    } catch (error) {
      logger.error('Error saving IT capabilities:', error);
      showNotification?.('Failed to save IT capabilities', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchCapabilities();
    setHasChanges(false);
  };

  const updateCapability = (path, value) => {
    setCapabilities(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!capabilities) {
    return <div className="p-4 text-gray-500">No IT capabilities configured</div>;
  }

  return (
    <div className="space-y-6">
      {/* Master Enable Toggle */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={capabilities.enabled || false}
            onChange={(e) => updateCapability('enabled', e.target.checked)}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <span className="ml-3 font-medium text-gray-900 dark:text-white">
            Enable IT Capabilities
          </span>
        </label>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Enable this agent to handle IT-related tasks including ticket management, access control, asset management, and security protocols.
        </p>
      </div>

      {capabilities.enabled && (
        <>
          {/* Ticket Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Ticket Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.ticketManagement?.enabled || false}
                  onChange={(e) => updateCapability('ticketManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable ticket management</span>
              </label>

              {capabilities.ticketManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.ticketManagement?.autoAssign || false}
                      onChange={(e) => updateCapability('ticketManagement.autoAssign', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-assign tickets</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.ticketManagement?.autoResolveCommon || false}
                      onChange={(e) => updateCapability('ticketManagement.autoResolveCommon', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-resolve common issues</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Access Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Access Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.accessManagement?.enabled || false}
                  onChange={(e) => updateCapability('accessManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable access management</span>
              </label>

              {capabilities.accessManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.accessManagement?.autoProvision || false}
                      onChange={(e) => updateCapability('accessManagement.autoProvision', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Auto-provision access</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.accessManagement?.requireApproval || false}
                      onChange={(e) => updateCapability('accessManagement.requireApproval', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Require approval for access requests</span>
                  </label>

                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Deactivation Policy</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.accessManagement?.deactivationPolicy?.autoDeactivate || false}
                        onChange={(e) => updateCapability('accessManagement.deactivationPolicy.autoDeactivate', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Auto-deactivate inactive users</span>
                    </label>

                    {capabilities.accessManagement?.deactivationPolicy?.autoDeactivate && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Days Inactive Before Deactivation</label>
                        <input
                          type="number"
                          value={capabilities.accessManagement?.deactivationPolicy?.daysInactive || 30}
                          onChange={(e) => updateCapability('accessManagement.deactivationPolicy.daysInactive', Number(e.target.value))}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-[#2a2a2a]"
                          min="1"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Asset Management */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Asset Management</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.assetManagement?.enabled || false}
                  onChange={(e) => updateCapability('assetManagement.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable asset management</span>
              </label>

              {capabilities.assetManagement?.enabled && (
                <>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.assetManagement?.trackDevices || false}
                      onChange={(e) => updateCapability('assetManagement.trackDevices', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Track devices</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.assetManagement?.softwareLicenses || false}
                      onChange={(e) => updateCapability('assetManagement.softwareLicenses', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Track software licenses</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.assetManagement?.warrantyTracking || false}
                      onChange={(e) => updateCapability('assetManagement.warrantyTracking', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Track warranties</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.assetManagement?.autoRenewAlerts || false}
                      onChange={(e) => updateCapability('assetManagement.autoRenewAlerts', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Send auto-renewal alerts</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Security Protocols */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Security Protocols</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.securityProtocols?.enabled || false}
                  onChange={(e) => updateCapability('securityProtocols.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable security protocols</span>
              </label>

              {capabilities.securityProtocols?.enabled && (
                <>
                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Password Resets</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.securityProtocols?.passwordResets?.enabled || false}
                        onChange={(e) => updateCapability('securityProtocols.passwordResets.enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Enable password reset handling</span>
                    </label>

                    {capabilities.securityProtocols?.passwordResets?.enabled && (
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={capabilities.securityProtocols?.passwordResets?.requireVerification || false}
                          onChange={(e) => updateCapability('securityProtocols.passwordResets.requireVerification', e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm">Require identity verification</span>
                      </label>
                    )}
                  </div>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={capabilities.securityProtocols?.mfaEnforcement || false}
                      onChange={(e) => updateCapability('securityProtocols.mfaEnforcement', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm">Enforce MFA requirements</span>
                  </label>

                  <div className="ml-6 space-y-3 border-l-2 border-gray-200 dark:border-gray-800 pl-4">
                    <h4 className="font-medium text-sm">Security Incidents</h4>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.securityProtocols?.securityIncidents?.autoEscalate || false}
                        onChange={(e) => updateCapability('securityProtocols.securityIncidents.autoEscalate', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Auto-escalate security incidents</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={capabilities.securityProtocols?.securityIncidents?.notifySecTeam || false}
                        onChange={(e) => updateCapability('securityProtocols.securityIncidents.notifySecTeam', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm">Notify security team</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Knowledge Base */}
          <div className="bg-white dark:bg-[#1a1a1a] p-6 rounded-lg border border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-semibold mb-4">Knowledge Base</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={capabilities.knowledgeBase?.enabled || false}
                  onChange={(e) => updateCapability('knowledgeBase.enabled', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="ml-2 text-sm">Enable knowledge base</span>
              </label>

              {capabilities.knowledgeBase?.enabled && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={capabilities.knowledgeBase?.autoSuggestArticles || false}
                    onChange={(e) => updateCapability('knowledgeBase.autoSuggestArticles', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="ml-2 text-sm">Auto-suggest knowledge articles</span>
                </label>
              )}
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-[#1a1a1a] p-4 border-t border-gray-200 dark:border-gray-800 -mx-6 -mb-6">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ITCapabilities;
